import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, Checkbox, FormControlLabel, Button, IconButton, InputAdornment, Divider, Box, Typography, Snackbar, Alert, CircularProgress, useMediaQuery, Tabs, Tab, Card, CardContent, Chip, Tooltip, Paper,  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LaunchIcon from "@mui/icons-material/Launch";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { RateField } from "../../../../../Components/RateField";
import { useMutation, useQueryClient, useQuery } from "react-query";
import API from "../../../../../API";
import { formatDateToYYYYMMDD, PickerToLocal, TimelineStatus } from "../../../../../Utils/constants";

/* helpers */
const money = (v) =>
  parseFloat(String(v ?? "0").replace(/[^0-9.,-]/g, "").replace(/,/g, "")) || 0;
const updVideo = (arr, id, k, v) =>
  arr.map((vid) => (vid.id === id ? { ...vid, [k]: v } : vid));

// Constants matching AppShell
const BAR_HEIGHT = 100;
const EXTRA_GUTTER = 17;

/* ---------- timeline entry component ---------- */
function TimelineEntry({ event, onEditField, campaignStatus, onDelete }) {
  const theme = useTheme();
  const isDraft = campaignStatus === 'Draft';
  const isComplete = event.status === 'complete';

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderLeft: `4px solid ${isComplete ? theme.palette.success.main : theme.palette.primary.main}`,
        opacity: event.will_delete ? 0.6 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[2]
        }
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <TextField
            value={event.objective || ''}
            variant="outlined"
            fullWidth
            placeholder="Task objective..."
            onChange={(e) => onEditField(event.id, 'objective', e.target.value)}
            disabled={!isDraft}
            sx={{ 
              '& .MuiInputBase-root': { fontSize: '1.125rem' },
              textDecoration: isComplete ? 'line-through' : 'none'
            }}
          />
          
          <Box display="flex" alignItems="center" gap={1} ml={2}>
            <Chip
              label={event.status || 'incomplete'}
              color={isComplete ? 'success' : 'default'}
              variant="outlined"
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
            <IconButton
              onClick={() => onDelete(event.id)}
              disabled={!isDraft}
              color="error"
            >
              {event.will_delete ? <DeleteIcon /> : <DeleteOutlineIcon />}
            </IconButton>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Notes"
              value={event.notes || ''}
              fullWidth
              multiline
              minRows={2}
              disabled={!isDraft}
              onChange={(e) => onEditField(event.id, 'notes', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Manager Notes"
              value={event.manager_notes || ''}
              fullWidth
              multiline
              minRows={2}
              onChange={(e) => onEditField(event.id, 'manager_notes', e.target.value)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              label="Due Date"
              type="date"
              value={event.projected_date ? formatDateToYYYYMMDD(new Date(event.projected_date)) : ''}
              disabled={!isDraft}
              onChange={(e) => onEditField(event.id, 'projected_date', e.target.valueAsDate)}
              sx={{ width: 160 }}
              InputLabelProps={{ shrink: true }}
            />
            
            <Tooltip title="Creator Delivered">
              <Checkbox
                checked={!!event.manager_completed}
                onChange={(e) => onEditField(event.id, 'manager_completed', e.target.checked)}
                color="primary"
              />
            </Tooltip>
          </Box>

          <IconButton
            onClick={() => onEditField(event.id, 'status', isComplete ? 'incomplete' : 'complete')}
            color={isComplete ? 'success' : 'default'}
          >
            {isComplete ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ---------- history sub-dialog ---------- */
function HistoryDialog({ open, onClose, loading, entries }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const LABELS = {
    price: "Price",
    status: "Status",
    agencyRate: "Agency Rate",
    assetsSent: "Deliverables Sent",
    assetsApproved: "Deliverables Approved",
    creatorBrief: "Creator Brief",
    postingInstructions: "Posting Instructions",
    skipPayout: "Skip Payment",
    payoutComplete: "Payment Completed",
    boostCode: "Boost Code",
    name: "Creator Name",
    submissionLink: "Submission Link",
  };

  const nice = (k, v) => {
    if (v == null || v === "") return "(empty)";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (["price", "agencyRate"].includes(k) && !isNaN(v))
      return `$${parseFloat(v).toFixed(2)}`;
    return v.toString();
  };

  const listLines = (changesRaw) => {
    const ch =
      typeof changesRaw === "string"
        ? JSON.parse(changesRaw || "{}")
        : changesRaw || {};
    const rows = [];
    Object.values(ch).forEach((sect) =>
      Object.entries(sect || {}).forEach(([path, diff]) => {
        const k = path.match(/\['([^']+)'\]$/)?.[1] || path;
        if (!LABELS[k]) return;
        rows.push(
          `${LABELS[k]}: ${nice(k, diff.old_value)} → ${nice(k, diff.new_value)}`
        );
      })
    );
    return rows.length ? rows : ["(no tracked field changes)"];
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          ...(isMobile && {
            mb: `calc(${BAR_HEIGHT + EXTRA_GUTTER}px + env(safe-area-inset-bottom, 0px))`,
          }),
        },
      }}
    >
      <DialogTitle>Creator History</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : entries.length === 0 ? (
          <Typography>No history entries.</Typography>
        ) : (
          entries.map((h) => (
            <Box key={h.id} mb={3}>
              <Typography variant="subtitle2">
                {new Date(h.timestamp).toLocaleString()} — {h.user_name}
              </Typography>
              <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                {listLines(h.changes).map((ln, i) => (
                  <li key={i}>{ln}</li>
                ))}
              </ul>
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------- template confirm dialog ---------- */
function TemplateConfirmDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Apply Template Timeline</DialogTitle>
      <DialogContent>
        <Typography>
          This will create a standard timeline template with the following tasks:
        </Typography>
        <ul style={{ marginTop: 8 }}>
          <li>Send script/video idea (Due in 3 days)</li>
          <li>Send draft/approve draft (Due in 4 days)</li>
          <li>Video posted (Due in 5 days)</li>
        </ul>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" autoFocus>
          Apply Template
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------- main dialog ---------- */
export default function CampaignCreatorEditDialog({
  open,
  onClose,
  creator,
  campaignId,
  index,
  handleUploadClick,
  onFieldChange, // update parent immediately for UI
  onDropCreator,
  creatorId,
  isTcc = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const qc = useQueryClient();

  const [draft, setDraft] = useState({ ...creator });
  const [dirty, setDirty] = useState(new Set());
  const [selectedTab, setSelectedTab] = useState(0);

  /* timeline state */
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineDirty, setTimelineDirty] = useState(new Set());
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  /* history */
  const [hOpen, setHOpen] = useState(false);
  const [hLoading, setHLoading] = useState(false);
  const [entries, setEntries] = useState([]);

  /* toast */
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  /* fetch timeline events for this creator */
  const { data: timelineData, refetch: refetchTimeline } = useQuery(
    ["creatorTimeline", campaignId, creator.id],
    () => API.campaigns.getCreatorTimeline(campaignId, creator.id),
    { 
      enabled: open && !!creator.id,
      refetchOnWindowFocus: false 
    }
  );

  /* sync when reopened or timeline data changes */
  useEffect(() => {
    if (open) {
      setDraft({ ...creator });
      setDirty(new Set());
      setTimelineDirty(new Set());
      setSelectedTab(0);
    }
  }, [open, creator]);

  useEffect(() => {
    if (timelineData) {
      setTimelineEvents(timelineData.map(event => ({
        ...event,
        projected_date: event.projected_date ? new Date(event.projected_date) : null,
        will_delete: false
      })));
    }
  }, [timelineData]);

  const flag = (k) => setDirty((s) => new Set(s).add(k));
  const setField = (k, v) => {
    flag(k);
    setDraft((d) => ({ ...d, [k]: v }));
  };
  const setVideo = (id, k, v) => {
    flag("videos");
    setDraft((d) => ({ ...d, videos: updVideo(d.videos ?? [], id, k, v) }));
  };

  /* timeline operations */
  const flagTimeline = (id) => setTimelineDirty(s => new Set(s).add(id));

  const updateTimelineEvent = (eventId, field, value) => {
    flagTimeline(eventId);
    setTimelineEvents(events => 
      events.map(event => 
        event.id === eventId 
          ? { ...event, [field]: value }
          : event
      )
    );
  };

  const deleteTimelineEvent = (eventId) => {
    const event = timelineEvents.find(e => e.id === eventId);
    if (event && event.id && !event.isNew) {
      // Mark existing event for deletion
      flagTimeline(eventId);
      setTimelineEvents(events =>
        events.map(e => 
          e.id === eventId 
            ? { ...e, will_delete: !e.will_delete }
            : e
        )
      );
    } else {
      // Remove new event entirely
      setTimelineEvents(events => events.filter(e => e.id !== eventId));
    }
  };

  const addTimelineEvent = (templateEvent = null) => {
    const newId = `new_${Date.now()}_${Math.random()}`;
    const baseEvent = {
      id: newId,
      campaign_id: campaignId,
      username: creator.id,
      objective: "",
      notes: "",
      manager_notes: "",
      projected_date: new Date(Date.now() + 24 * 60 * 60 * 1000), // tomorrow
      status: "incomplete",
      manager_completed: false,
      will_delete: false,
      isNew: true
    };

    const newEvent = templateEvent ? { ...baseEvent, ...templateEvent, id: newId } : baseEvent;
    setTimelineEvents(prev => [...prev, newEvent]);
    flagTimeline(newId);
  };

  const applyTemplate = () => {
    const today = new Date();
    const templates = [
      {
        objective: 'Send script/video idea',
        projected_date: new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000),
      },
      {
        objective: 'Send draft/approve draft', 
        projected_date: new Date(today.getTime() + 4 * 24 * 60 * 60 * 1000),
      },
      {
        objective: 'Video posted',
        projected_date: new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000),
      }
    ];

    templates.forEach(template => addTimelineEvent(template));
    setTemplateDialogOpen(false);
  };

  /* mutation – saves creator data */
  const { mutateAsync: saveCreator, isLoading: savingCreator } = useMutation(
    (payload) => API.campaigns.updateCampaignCreatorSpecs(payload),
    {
      onSuccess: () => {
        qc.invalidateQueries(["campaign", campaignId]);
        setToast({ open: true, sev: "success", msg: "Creator details saved!" });
      },
      onError: (e) =>
        setToast({
          open: true,
          sev: "error",
          msg: e.response?.data?.error || e.message,
        }),
    }
  );

  /* mutation – saves timeline */
  const { mutateAsync: saveTimeline, isLoading: savingTimeline } = useMutation(
    ({ campaignId, creatorId, events }) =>
      API.creatorConnect.campaignUpdateTimeline(campaignId, creatorId, events),
    {
      onSuccess: () => {
        refetchTimeline();
        setToast({ open: true, sev: "success", msg: "Timeline saved!" });
      },
      onError: (e) =>
        setToast({
          open: true,
          sev: "error", 
          msg: e.response?.data?.error || e.message,
        }),
    }
  );

  /* save handler */
  const handleSave = async () => {
    const promises = [];

    // Save creator details if dirty
    if (dirty.size > 0) {
      const patch = {};
      dirty.forEach((k) => {
        patch[k] = ["price", "agencyRate"].includes(k) ? money(draft[k]) : draft[k];
      });
      if (patch.assetsApproved) patch.status = "Approved";

      promises.push(
        saveCreator({
          campaignId,
          creators: [{ id: creator.id, ...patch }],
        }).then(() => {
          // Update parent state WITHOUT triggering Unsaved flag
          Object.entries(patch).forEach(([k, v]) =>
            onFieldChange(k, v, false /* no unsaved */)
          );
        })
      );
    }

    // Save timeline if dirty
    if (timelineDirty.size > 0) {
      const eventsToSave = timelineEvents
        .filter(event => !event.will_delete)
        .map(event => ({
          ...event,
          projected_date: event.projected_date?.toISOString(),
          // Remove client-side only fields
          isNew: undefined,
          will_delete: undefined
        }));

      promises.push(
        saveTimeline({
          campaignId,
          creatorId: creator.id,
          events: eventsToSave
        })
      );
    }

    if (promises.length === 0) {
      return onClose();
    }

    try {
      await Promise.all(promises);
      onClose();
    } catch (error) {
      // Error handling is done in individual mutations
    }
  };

  /* history fetch */
  const openHistory = async () => {
    if (hOpen) return setHOpen(true);
    setHLoading(true);
    setHOpen(true);
    try {
      const res = await API.campaigns.creatorHistory(campaignId, creatorId);
      setEntries(res || []);
    } catch (err) {
      setToast({
        open: true,
        sev: "error",
        msg: err.response?.data?.error || "Failed to load history",
      });
    } finally {
      setHLoading(false);
    }
  };

  const parentVids = (draft.videos ?? []).filter((v) => !v.parentId);
  const hasUnsavedChanges = dirty.size > 0 || timelineDirty.size > 0;
  const isLoading = savingCreator || savingTimeline;

  const sortedTimelineEvents = timelineEvents
    .filter(event => !event.will_delete)
    .sort((a, b) => {
      if (!a.projected_date) return 1;
      if (!b.projected_date) return -1;
      return new Date(a.projected_date) - new Date(b.projected_date);
    });

  const isDraft = true; // You might want to pass campaign status as a prop

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="lg"
      PaperProps={{
        sx: {
          ...(isMobile && {
            mb: `calc(${BAR_HEIGHT + EXTRA_GUTTER}px + env(safe-area-inset-bottom, 0px))`,
          }),
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Edit Creator: {creator.name || creator.id}
        <IconButton size="small" onClick={openHistory}>
          <HistoryIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={selectedTab} 
          onChange={(_, newValue) => setSelectedTab(newValue)}
          centered
        >
          <Tab label="Details" />
          <Tab label="Timeline" />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ minHeight: 400 }}>
        {/* DETAILS TAB */}
        {selectedTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <RateField
                label="Price"
                value={draft.price}
                fullWidth
                onChange={(v) => setField("price", v)}
              />
            </Grid>
            {isTcc && (
              <Grid item xs={12} md={6}>
                <RateField
                  label="Agency Rate"
                  value={draft.agencyRate}
                  fullWidth
                  onChange={(v) => setField("agencyRate", v)}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!draft.assetsSent}
                    onChange={(e) => setField("assetsSent", e.target.checked)}
                  />
                }
                label="Deliverables Sent"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!draft.assetsApproved}
                    disabled={!draft.assetsSent}
                    onChange={(e) =>
                      setField("assetsApproved", e.target.checked)
                    }
                  />
                }
                label="Approved"
                sx={{ ml: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!draft.skipPayout}
                    onChange={(e) => setField("skipPayout", e.target.checked)}
                  />
                }
                label="Skip Payment"
                sx={{ ml: 2 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Creator Brief"
                value={draft.creatorBrief ?? ""}
                multiline
                minRows={3}
                fullWidth
                onChange={(e) => setField("creatorBrief", e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleUploadClick(index)}>
                        <CloudUploadIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {draft.submissionLink && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<LaunchIcon />}
                  onClick={() =>
                    window.open(draft.submissionLink, "_blank", "noopener")
                  }
                >
                  Open Submission Folder
                </Button>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Posting Instructions"
                value={draft.postingInstructions ?? ""}
                multiline
                minRows={3}
                fullWidth
                onChange={(e) =>
                  setField("postingInstructions", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ mt: 3, mb: 1 }}>Video Links & Boost Codes</Divider>
              {parentVids.map((v) => (
                <Box key={v.id} mb={2}>
                  <Typography variant="subtitle2">Video #{v.id}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Live Link"
                        value={v.liveLink ?? ""}
                        onChange={(e) => setVideo(v.id, "liveLink", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Boost Code"
                        value={v.boostCode ?? ""}
                        onChange={(e) => setVideo(v.id, "boostCode", e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Grid>
          </Grid>
        )}

        {/* TIMELINE TAB */}
        {selectedTab === 1 && (
          <Box>
            <Box display="flex" justifyContent="space-between" mb={3}>
              <Typography variant="h6">Tasks & Timeline</Typography>
              {sortedTimelineEvents.length === 0 && isDraft && (
                <Button 
                  variant="contained" 
                  onClick={() => setTemplateDialogOpen(true)}
                >
                  Apply Template
                </Button>
              )}
            </Box>

            {sortedTimelineEvents.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No timeline events created yet.
                </Typography>
              </Paper>
            ) : (
              <Box>
                {sortedTimelineEvents.map((event) => (
                  <TimelineEntry
                    key={event.id}
                    event={event}
                    onEditField={updateTimelineEvent}
                    campaignStatus={isDraft ? 'Draft' : 'Launched'}
                    onDelete={deleteTimelineEvent}
                  />
                ))}
              </Box>
            )}

            {isDraft && (
              <Box textAlign="center" mt={4}>
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => addTimelineEvent()}
                  sx={{
                    borderStyle: 'dashed',
                    '&:hover': { borderStyle: 'dashed' }
                  }}
                >
                  Add New Task
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <IconButton color="error" onClick={onDropCreator}>
          <DeleteIcon />
        </IconButton>
        <Box flexGrow={1} />
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || !hasUnsavedChanges}
        >
          {isLoading ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>

      {/* Sub-dialogs */}
      <HistoryDialog
        open={hOpen}
        onClose={() => setHOpen(false)}
        loading={hLoading}
        entries={entries}
      />
      
      <TemplateConfirmDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onConfirm={applyTemplate}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.sev}
          sx={{ width: "100%" }}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}